import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createRegion,
  getAllRegions,
  getRegionByID,
  updateRegion,
} from "./services/region.services";
import { clearRegionDetails } from "./regionSlice";
import { closed } from "../../components/drawer/drawerSlice";

const CreateRegion: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const RegionDetail = useAppSelector((state) => state.region.RegionDetail);
  const RegionID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.region.formLoading);

  useEffect(() => {
    if (RegionID) {
      dispatch(getRegionByID(RegionID));
    }
  }, [RegionID]);

  useEffect(() => {
    return () => {
      dispatch(clearRegionDetails());
      dispatch(closed());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (!RegionID) {
        const insertPayload = {
          RegionName: values.RegionName,
          IsActive: true,
        };
        const response = await dispatch(createRegion(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearRegionDetails());
          dispatch(getAllRegions());
        }
      } else {
        const editPayload = {
          ID: RegionID,
          RegionName: values.RegionName,
          IsActive: values.IsActive,
        };
        const response = await dispatch(updateRegion(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearRegionDetails());
          dispatch(getAllRegions());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={RegionDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="RegionName"
                label="Region"
                component={FormTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginRight: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
              <ButtonWithLoading
                label={RegionID ? "Update" : "Create"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateRegion;
